import { useEffect, useRef, useState } from "react";
import { useBannerForm } from "../../contexts/BannerFormContext";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { getAllCategory } from "../../redux/slices/categorySlice";
import Select from "react-select";
import { toast } from "react-toastify";
import Lottie from "lottie-react";
import buttonLoading from "../../animations/buttonLoading.json";
import { IoIosCloseCircle } from "react-icons/io";
import { createBanner } from "../../redux/slices/bannerSlice";
import { getErrorMessage } from "../../utils/errorHandler";
import { useFileUpload } from "../../hooks/useFileUpload";
import { API } from "../../configs/endpoints";
import type { Image } from "../../types/common/image";

type PropTypes = {
  onBack: () => void;
};
export const CreateBannerContent = ({ onBack }: PropTypes) => {
  const { formBanner, setFormBanner, resetForm } = useBannerForm();
  const dispatch = useAppDispatch();
  const { allCategory } = useAppSelector((state) => state.category);
  useEffect(() => {
    dispatch(getAllCategory({}));
  }, [dispatch]);

  const inputRef = useRef<HTMLInputElement>(null);
  const { upload, isUploading: isChooseImage } = useFileUpload<{
    image: Image;
  }>({
    endpoint: API.banner.upload,
    kind: "image",
  });
  const handleChooseImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    const result = await upload(file);
    e.target.value = "";
    if (result.ok && result.data?.image) {
      setFormBanner((prev) => ({ ...prev, image: result.data!.image }));
    }
  };

  const [isSubmit, setIsSubmit] = useState<boolean>(false);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formBanner.title?.trim()) {
      toast.error("Tiêu đề không được để trống");
      return;
    }
    if (!formBanner.image?.url) {
      toast.error("Vui lòng chọn ảnh banner");
      return;
    }
    if (formBanner.position === "category" && !formBanner.categoryId) {
      toast.error("Vui lòng chọn danh mục");
      return;
    }
    // home_top không gắn category → ép null để khỏi gửi rác lên server
    const payload = {
      ...formBanner,
      categoryId:
        formBanner.position === "home_top" ? null : formBanner.categoryId,
    };
    setIsSubmit(true);
    try {
      await dispatch(createBanner(payload)).unwrap();
      toast.success("Tạo banner thành công");
      onBack();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsSubmit(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="p-5 space-y-2">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onBack}
          className="text-sm text-blue-500 hover:underline transition-colors duration-300 cursor-pointer"
        >
          Quản lý banner
        </button>
        <p className="text-sm text-gray-700">/</p>
        <p className="text-sm text-gray-700">Thêm banner</p>
      </div>
      <div className="p-5 bg-white rounded-xl border border-gray-100 space-y-2">
        <h1 className="text-lg font-bold text-black">Thêm banner</h1>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-2">
              <div className="space-y-2">
                <label className="block text-sm text-gray-700 font-semibold">
                  Tiêu đề
                </label>
                <input
                  type="text"
                  value={formBanner.title}
                  onChange={(e) =>
                    setFormBanner((prev) => ({
                      ...prev,
                      title: e.target.value,
                    }))
                  }
                  className="w-full p-2 focus:outline-none border border-gray-200 rounded-xl focus:border-blue-500 transition-colors duration-300 placeholder:text-sm placeholder:tracking-tighter placeholder:text-gray-400"
                  placeholder="Nhập tiêu đề banner"
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm text-gray-700 font-semibold">
                  Định hướng
                </label>
                <select
                  value={formBanner.orientation}
                  onChange={(e) =>
                    setFormBanner((prev) => ({
                      ...prev,
                      orientation: e.target.value as "horizontal" | "vertical",
                      image: null,
                    }))
                  }
                  className="p-2 text-sm text-gray-700 focus:outline-none focus:border-blue-500 transition-colors duration-300 cursor-pointer hover:border-blue-500 border border-gray-200 rounded-xl"
                >
                  <option value="horizontal">Ngang</option>
                  <option value="vertical">Dọc</option>
                </select>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <label className="block text-sm text-gray-700 font-semibold">
                    Đường link liên kết
                  </label>
                  <p className="text-xs text-gray-700 tracking-tighter">
                    (không bắt buộc)
                  </p>
                </div>
                <input
                  type="text"
                  value={formBanner.link ?? ""}
                  onChange={(e) =>
                    setFormBanner((prev) => ({
                      ...prev,
                      link: e.target.value,
                    }))
                  }
                  className="w-full p-2 focus:outline-none border border-gray-200 rounded-xl focus:border-blue-500 transition-colors duration-300 placeholder:text-sm placeholder:tracking-tighter placeholder:text-gray-400"
                  placeholder="Nhập đường link liên kết"
                />
              </div>
            </div>
            <div className="space-y-2">
              <div className="space-y-2">
                <label className="block text-sm text-gray-700 font-semibold">
                  Vị trí
                </label>
                <select
                  value={formBanner.position}
                  onChange={(e) =>
                    setFormBanner((prev) => ({
                      ...prev,
                      position: e.target.value as "home_top" | "category",
                    }))
                  }
                  className="p-2 text-sm text-gray-700 focus:outline-none focus:border-blue-500 transition-colors duration-300 cursor-pointer hover:border-blue-500 border border-gray-200 rounded-xl"
                >
                  <option value="home_top">Trang chủ</option>
                  <option value="category">Danh mục</option>
                </select>
              </div>
              <div className="space-y-2">
                <label className="block text-sm text-gray-700 font-semibold">
                  Trạng thái
                </label>
                <select
                  value={String(formBanner.isActive)}
                  onChange={(e) =>
                    setFormBanner((prev) => ({
                      ...prev,
                      isActive: e.target.value === "true",
                    }))
                  }
                  className="p-2 text-sm text-gray-700 focus:outline-none focus:border-blue-500 transition-colors duration-300 cursor-pointer hover:border-blue-500 border border-gray-200 rounded-xl"
                >
                  <option value="true">Hoạt động</option>
                  <option value="false">Ngừng hoạt động</option>
                </select>
              </div>
              <div className="space-y-2">
                <label className="block text-sm text-gray-700 font-semibold">
                  Danh mục
                </label>
                <Select
                  className="text-sm w-full"
                  options={allCategory.map((cate) => ({
                    value: cate._id,
                    label: cate.name,
                  }))}
                  value={
                    allCategory
                      .map((cate) => ({
                        value: cate._id,
                        label: cate.name,
                      }))
                      .find((cate) => cate.value === formBanner.categoryId) ||
                    null
                  }
                  onChange={(selected) => {
                    setFormBanner((prev) => ({
                      ...prev,
                      categoryId: selected?.value ?? null,
                    }));
                  }}
                  placeholder="Tìm và chọn danh mục..."
                  isSearchable
                  required
                />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div className="space-y-2 col-span-1">
              <label className="block text-sm text-gray-700 font-semibold">
                Chọn ảnh
              </label>
              <input
                type="file"
                className="hidden"
                ref={inputRef}
                onChange={handleChooseImage}
              />
              {isChooseImage ? (
                <Lottie
                  animationData={buttonLoading}
                  loop={true}
                  className="h-10 w-20"
                />
              ) : (
                <button
                  type="button"
                  disabled={formBanner.image?.public_id ? true : false}
                  onClick={() => inputRef.current?.click()}
                  className={`p-2 text-sm rounded-lg transition-colors duration-300 cursor-pointer ${formBanner.image?.public_id ? "bg-gray-200 pointer-events-none text-gray-400" : "text-white bg-blue-500 hover:bg-blue-600 pointer-events-auto"}`}
                >
                  Thêm ảnh
                </button>
              )}
            </div>
            <div className="w-full col-span-2">
              {formBanner.orientation === "horizontal" &&
                formBanner.image?.public_id && (
                  <div className="w-full h-30 border border-gray-200 rounded-xl relative">
                    <img
                      src={formBanner.image.url}
                      alt="Ảnh banner"
                      className="w-full h-full object-cover rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setFormBanner((prev) => ({
                          ...prev,
                          image: null,
                        }));
                      }}
                      className="text-lg text-red-500 hover:text-red-600 transition-colors duration-300 absolute -top-2 -right-2 cursor-pointer"
                    >
                      <IoIosCloseCircle />
                    </button>
                  </div>
                )}
              {formBanner.orientation === "vertical" &&
                formBanner.image?.public_id && (
                  <div className="w-30 h-72 border border-gray-200 rounded-xl relative">
                    <img
                      src={formBanner.image.url}
                      alt="Ảnh banner"
                      className="w-full h-full object-cover rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setFormBanner((prev) => ({
                          ...prev,
                          image: null,
                        }));
                      }}
                      className="text-lg text-red-500 hover:text-red-600 transition-colors duration-300 absolute -top-2 -right-2 cursor-pointer"
                    >
                      <IoIosCloseCircle />
                    </button>
                  </div>
                )}
            </div>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between">
        <div className="">
          <button
            type="button"
            onClick={onBack}
            className="p-2 text-sm text-white bg-blue-500 cursor-pointer hover:bg-blue-600 rounded-lg transition-colors duration-300"
          >
            Quay lại
          </button>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              resetForm();
            }}
            className="p-2 text-sm bg-red-500 rounded-lg cursor-pointer hover:bg-red-600 text-white transition-colors duration-300"
          >
            Hủy
          </button>
          {isSubmit ? (
            <Lottie
              animationData={buttonLoading}
              loop={true}
              className="h-10 w-20"
            />
          ) : (
            <button
              type="submit"
              className="p-2 text-sm bg-green-500 rounded-lg cursor-pointer hover:bg-green-600 text-white transition-colors duration-300"
            >
              Thêm banner
            </button>
          )}
        </div>
      </div>
    </form>
  );
};
